/**
 * Centralized layout style definitions for HCO (Healthcare Organization) pages
 *
 * These layout classes are used across HCO feature components for consistent spacing and layout.
 * All spacing values are defined here to maintain visual consistency and enable easy updates.
 *
 * NOTE: These styles are applied at the page component level, not in App.tsx, because:
 * - Many pages need custom layouts (drawers, height overrides, width constraints)
 * - Pages create their own wrapper structures that override App.tsx defaults
 * - This approach provides flexibility while maintaining consistency through shared constants
 */

/**
 * Main content area styles
 * Used for Tailwind-styled pages (v2 and newer Tailwind pages) that use <main> elements
 * Provides complete layout solution for scrollable content areas:
 * - flex-1: takes remaining vertical space
 * - overflow-y-auto: enables vertical scrolling
 * - mx-auto: centers horizontally
 * - mt-3: top margin (0.75rem / 12px)
 * - pl-9: left padding (2.25rem / 36px) on desktop
 * - pr-4: right padding (1rem / 16px) on desktop
 * - px-3: equal left/right padding (0.75rem / 12px) on mobile
 * - w-full: full width
 *
 * Usage: <main className={MAIN_CONTENT_AREA}>...</main>
 *
 * use this style for all v2 pages, should be the default moving forward
 */
export const MAIN_CONTENT_AREA =
  'tw:flex-1 tw:overflow-y-auto tw:mx-auto tw:mt-3 tw:max-lg:px-3 tw:pl-9 tw:pr-4 tw:w-full';

/**
 * Page header section styles
 * Used for header sections with title and action buttons
 * - flex: flexbox layout
 * - items-center: vertically center items
 * - justify-between: space items apart horizontally
 * - mb-3: bottom margin (0.75rem / 12px)
 */
export const PAGE_HEADER = 'tw:flex tw:items-center tw:justify-between tw:mb-3';

/**
 * Page header section styles with vertical margin
 * Used when header needs both top and bottom margin
 * - flex: flexbox layout
 * - items-center: vertically center items
 * - justify-between: space items apart horizontally
 * - my-3: vertical margin (0.75rem / 12px top and bottom)
 */
export const PAGE_HEADER_WITH_VERTICAL_MARGIN =
  'tw:flex tw:items-center tw:justify-between tw:mb-3';
